import React, {useState} from "react";
import classnames from 'classnames'
import {useDealContext} from "../../contexts/DealContext";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Tooltip from "@mui/material/Tooltip";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import {DealRebateModal} from "./DealRebateModal";
import CurrencyInput from "react-currency-input-field";
import Checkbox from "@mui/material/Checkbox";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import {useMediaQuery} from "react-responsive";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import useRebateStateHook from "../../hooks/useRebateStateHook";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";

import Stack from "@mui/material/Stack";

export const DealPageRebateItem = ({
   rebate,
}) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [rebateState, setRebateStateWithUpdate] = useRebateStateHook(rebate)
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const {updateDealResponse, isHistorical} = useDealContext()
    const [isRebateModalOpen, setIsRebateModalOpen] = useState(false)

    const isLoading = updateDealResponse?.loading
    const isStackable = rebateState.is_compatible
    const isCustom = rebateState.is_custom

    const handleClick = (event) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <TableRow>
            <TableCell
                align="left"
                width={isTabletOrMobile ? '1%' : '40%'}
            >
                <span className={'sc-name'}>
                    {rebate.name}
                </span>
                {
                    isRebateModalOpen &&
                    <DealRebateModal
                        rebate={rebate}
                        isOpen={isRebateModalOpen}
                        setIsOpen={setIsRebateModalOpen}
                        name={rebateState.name}
                        description={rebateState.description}
                        expirationDate={rebateState.expiration_date}
                    />
                }
            </TableCell>
            {/* ── Mobile: amount + 3-dot Applied/Taxed menu ── */}
            {
                isTabletOrMobile &&
                <TableCell align="right" width={'50%'}>
                    <div
                        className={'rebate-amount-cell'}
                        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end' }}
                    >
                        <CurrencyInput
                            className={'payment-row_value disabled'}
                            prefix="$"
                            value={rebateState.amount}
                            disabled={true}
                            style={{ width: '100%' }}
                        />
                        <div>
                            <MoreVertIcon onClick={handleClick}/>
                            <Popover
                                onClick={(e) => e.stopPropagation()}
                                open={Boolean(anchorEl)}
                                anchorEl={anchorEl}
                                onClose={handleClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="center" style={{ padding: "5px 10px" }}>
                                    <Typography>Applied</Typography>
                                    <Checkbox
                                        className={classnames('payment-row_value', { isDisabled: isLoading || !rebateState.is_applied })}
                                        onChange={() => setRebateStateWithUpdate({...rebateState, is_applied: !rebateState.is_applied}, true)}
                                        checked={rebateState.is_applied}
                                        disabled={isLoading || isHistorical}
                                    />
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" style={{ padding: "5px 10px" }}>
                                    <Typography>Taxed</Typography>
                                    <Checkbox
                                        className={classnames('payment-row_value', { isDisabled: isLoading || !rebateState.is_applied })}
                                        onChange={() => setRebateStateWithUpdate({...rebateState, is_taxed: !rebateState.is_taxed}, true)}
                                        checked={rebateState.is_applied && rebateState.is_taxed}
                                        disabled={isLoading || !rebateState.is_applied || isHistorical}
                                    />
                                </Stack>
                            </Popover>
                        </div>
                    </div>
                </TableCell>
            }

            {/* ── Desktop: Icons | Applied | Taxed | Amount ── */}
            {
                !isTabletOrMobile &&
                <React.Fragment>
                    <TableCell align="center" width={'15%'}>
                        <span className={'rebate-row-icons'}>
                            <span className={'rebate-icon-slot'}>
                                {
                                    !isCustom &&
                                    // Empty title = no tooltip, so the historical
                                    // icon offers nothing and does nothing.
                                    <Tooltip title={isHistorical ? '' : 'Details'} arrow placement={'top'}>
                                        <DescriptionOutlinedIcon
                                            className={classnames('DescriptionIcon', {'is-static': isHistorical})}
                                            onClick={isHistorical ? undefined : () => setIsRebateModalOpen(!isRebateModalOpen)}
                                        />
                                    </Tooltip>
                                }
                            </span>
                            <span className={'rebate-icon-slot'}>
                                {
                                    isStackable &&
                                    <Tooltip title="Stackable" arrow placement={'top'}>
                                        <ContentCopyIcon className={'sc-stack'}/>
                                    </Tooltip>
                                }
                            </span>
                        </span>
                    </TableCell>
                    <TableCell width={'15%'} align="center">
                        <Tooltip title={rebateState.is_applied ? "Applied — click to remove" : "Apply this rebate"} arrow placement={'top'}>
                            <Checkbox
                                className={'payment-row_value'}
                                onChange={() => setRebateStateWithUpdate({...rebateState, is_applied: !rebateState.is_applied}, true)}
                                checked={rebateState.is_applied}
                                disabled={isLoading || isHistorical}
                            />
                        </Tooltip>
                    </TableCell>
                    <TableCell align="center" width={'15%'}>
                        <Tooltip title="Taxed as part of the sale" arrow placement={'top'}>
                            <Checkbox
                                className={classnames('payment-row_value', { isDisabled: isLoading || !rebateState.is_applied })}
                                onChange={() => setRebateStateWithUpdate({...rebateState, is_taxed: !rebateState.is_taxed}, true)}
                                checked={rebateState.is_applied && rebateState.is_taxed}
                                disabled={isLoading || !rebateState.is_applied || isHistorical}
                            />
                        </Tooltip>
                    </TableCell>
                    <TableCell align="center" width={'15%'}>
                        <CurrencyInput
                            className={'payment-row_value disabled rebate-amount-input'}
                            prefix="$"
                            value={rebateState.amount}
                            disabled={true}
                        />
                    </TableCell>
                </React.Fragment>
            }
        </TableRow>
    )
}